import { useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useSatelliteWebLandlordApplicationHandler_inviteToApply } from '@zg-rentals/ts-satellite-client-react-query';
import { ModalType } from '../types/ModalType';
import { APPLICATIONS_UPSELL_MODAL_COOKIE } from '../components/Modals/ApplicationsUpsellModal';
import { AppContext } from '../contexts/AppContextProvider';
import useModal from './useModal';
import type { ACTION_TEXT } from '../utils/getApplicationActionText';
import getApplicationActionText from '../utils/getApplicationActionText';
import { appMarkerLogger } from '../bootstrap';
import { patchConversationMessagesNote, useConversationMessagesQuery } from './useConversationMessagesQuery';
import type { ConversationMessagesData } from '../utils/conversationMessageUtils';

const logger = appMarkerLogger.child({ name: 'useInviteToApply' });

type UseInviteToApplyArgs = {
  conversationId: string;
  clientTimeZone: string;
  listingAlias: string;
  shouldShowApplicationUpsellModal: boolean;
};

export type InviteToApplyData = {
  isApplicationAlreadySent: boolean;
  isApplicationCompleted: boolean;
  isApplicationMarkedSent: boolean;
  isApplicationsAccepted: boolean;
  isApplicationsAllowed: boolean;
  isConversationArchived: boolean;
  isConversationBlockedByZillow: boolean;
  isConversationMarkedAsSpam: boolean;
  renterEmail: string;
  renterName: string;
};

export const selectInviteToApplyData = (data: ConversationMessagesData): InviteToApplyData => ({
  isApplicationAlreadySent: data.note.isApplicationAlreadySent,
  isApplicationCompleted: data.isApplicationCompleted,
  isApplicationMarkedSent: data.note.isApplicationMarkedSent,
  isApplicationsAccepted: data.isApplicationsAccepted,
  isApplicationsAllowed: data.isApplicationsAllowed,
  isConversationArchived: data.status.isArchived,
  isConversationBlockedByZillow: data.isConversationBlockedByZillow,
  isConversationMarkedAsSpam: data.status.isSpam,
  renterEmail: data.renterEmail ?? '',
  renterName: data.renterName ?? '',
});

/**
 *
 * @description useInviteToApply offers helpers to perform sending an application to a lead
 */
export default ({
  conversationId,
  clientTimeZone,
  listingAlias,
  shouldShowApplicationUpsellModal,
}: UseInviteToApplyArgs) => {
  const { paths } = useContext(AppContext);
  const { setActiveModal } = useModal();
  const [cookies] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);
  const queryClient = useQueryClient();

  // Cache hit on the inbox (MessageListItems owns the fetch + polling); fetches once on the
  // contacts surface, where SendApplicationConfirmationModal renders with an empty cache.
  const { data: conversationData, isPending: isLoading } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    fetchIfCacheEmpty: true,
    select: selectInviteToApplyData,
  });

  const { mutateAsync: inviteToApply } = useSatelliteWebLandlordApplicationHandler_inviteToApply({
    meta: {
      context: {
        api: 'inviteToApply',
        details: 'useInviteToApply - invite renter to apply',
      },
    },
  });

  const {
    isApplicationAlreadySent,
    isApplicationCompleted,
    isApplicationMarkedSent,
    isApplicationsAccepted,
    isApplicationsAllowed,
    isConversationArchived,
    isConversationBlockedByZillow,
    isConversationMarkedAsSpam,
    renterEmail,
    renterName,
  } = conversationData || {};
  const { applicationsUrl } = paths;
  const viewApplicationsForListingUrl = `${applicationsUrl}/property/${listingAlias}`;

  const [actionText, setActionText] = useState<ACTION_TEXT | null>(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [helperText, setHelperText] = useState({
    text: '',
    isFailure: false,
  });

  useEffect(() => {
    // Reset helper text when conversationId changes
    setHelperText({
      text: '',
      isFailure: false,
    });
  }, [conversationId]);

  useEffect(() => {
    const applicationActionText = getApplicationActionText({
      isApplicationCompleted: !!isApplicationCompleted,
      hasSentApplication: !!(isApplicationAlreadySent || isApplicationMarkedSent),
    });
    setActionText(applicationActionText);
  }, [isApplicationAlreadySent, isApplicationCompleted, isApplicationMarkedSent]);

  const handleInviteToApplyClick = async (isRetry = false): Promise<void> => {
    setIsSendingInvite(true);
    setHelperText({ text: '', isFailure: false });

    const nameParts = renterName?.split(' ') || [];
    const postData = {
      listingAlias: listingAlias || '',
      renterFirstName: nameParts[0] || '',
      // Send empty string when name has no space; avoids sending undefined (old GraphQL behavior) or duplicating the first name
      renterLastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
      toEmail: renterEmail || '',
      resendEmail: true,
    };

    logger.info(postData, 'Handling invite to apply');

    try {
      await inviteToApply({ postData });

      await patchConversationMessagesNote(
        queryClient,
        { conversationId, clientTimeZone },
        { isApplicationAlreadySent: true },
      );

      setHelperText({ text: 'Invite sent!', isFailure: false });

      // Allow landlords a chance to enable applications if it's not enabled
      // for their listing by showing them the Applications Upsell Modal.
      // The modal will only appear once.
      if (shouldShowApplicationUpsellModal && !isApplicationsAccepted && !cookies[APPLICATIONS_UPSELL_MODAL_COOKIE]) {
        setActiveModal(ModalType.ApplicationsUpsellModal);
      }
    } catch {
      if (!isRetry) {
        return handleInviteToApplyClick(true);
      }

      setHelperText({
        text: 'There was an error sending your invitation. Please try again.',
        isFailure: true,
      });
    }

    setIsSendingInvite(false);
  };

  return {
    actionText,
    handleInviteToApplyClick,
    helperText,
    isApplicationsAllowed,
    isConversationArchived,
    isConversationBlockedByZillow,
    isConversationMarkedAsSpam,
    isLoading,
    isSendingInvite,
    viewApplicationsForListingUrl,
  };
};
